import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Badge, Button, Card } from '../components/ui';
import { encouragementService } from '../services/api';

const ClientMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMessages = async (showUnreadOnly) => {
    try {
      setLoading(true);
      const response = await encouragementService.getReceived({
        limit: 50,
        unreadOnly: showUnreadOnly
      });

      setMessages(response.data?.messages || []);
      setUnreadCount(response.data?.unreadCount || 0);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Unable to load encouragement messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages(unreadOnly);
  }, [unreadOnly]);

  const handleMarkRead = async (messageId) => {
    try {
      await encouragementService.markRead(messageId);
      setMessages((prev) => prev.map((message) => (
        message._id === messageId ? { ...message, status: 'read', readAt: new Date().toISOString() } : message
      )));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error('Failed to mark message as read:', err);
      setError('Unable to update message status. Please try again.');
    }
  };

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6">
          <Badge variant="primary" size="sm">Encouragement Notes</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Messages from Your Support Team
          </h2>
          <p className="mt-2 text-neutral-600">
            Your clinician or clergy supporter may send brief, encouragement-only messages based on your progress.
          </p>
        </div>

        <Card className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm text-neutral-500">Unread Messages</p>
              <p className="text-2xl font-semibold text-neutral-900">{unreadCount}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={unreadOnly ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setUnreadOnly((prev) => !prev)}
              >
                {unreadOnly ? 'Showing Unread' : 'Show Unread Only'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </Card>

        {error && (
          <Card className="mb-6 border-error-200 bg-error-50 text-error-700">
            <p className="text-sm">{error}</p>
          </Card>
        )}

        <div className="space-y-4">
          {loading && (
            <Card>
              <p className="text-sm text-neutral-500">Loading messages...</p>
            </Card>
          )}

          {!loading && messages.map((message) => (
            <Card key={message._id} className="border border-neutral-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {message.clinicianId?.firstName} {message.clinicianId?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {message.sentAt ? new Date(message.sentAt).toLocaleDateString() : 'Unknown date'}
                  </p>
                </div>
                <Badge variant={message.readAt ? 'neutral' : 'success'} size="sm">
                  {message.readAt ? 'Read' : 'New'}
                </Badge>
              </div>
              <p className="mt-3 text-sm text-neutral-700">{message.messageText}</p>
              {(message.scriptureReference || message.scriptureText) && (
                <p className="mt-2 text-xs text-neutral-500">
                  Scripture: {message.scriptureReference || '—'} — {message.scriptureText || '—'}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
                  Template {message.templateId}
                </span>
                <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs text-neutral-600">
                  {message.templateCategory}
                </span>
                {!message.readAt && (
                  <Button variant="secondary" size="sm" onClick={() => handleMarkRead(message._id)}>
                    Mark as Read
                  </Button>
                )}
              </div>
            </Card>
          ))}

          {!loading && messages.length === 0 && (
            <Card>
              <p className="text-sm text-neutral-500">No encouragement messages yet.</p>
            </Card>
          )}
        </div>
      </main>
    </>
  );
};

export default ClientMessages;
